// Type-mismatch diagnostics (Item 3e) — shared type surface.
//
// The producer is a PURE module: it consumes already-resolved comparisons
// (column operands carry their coarse `OperandLiteralSqlType`, resolved from
// the schema snapshot one layer up) and emits engine-agnostic
// `TypeCheckMarker`s addressed by UTF-16 offset. The SqlMonaco layer converts
// offsets → Monaco positions via `model.getPositionAt`, so this module never
// touches Monaco and never re-derives line/column (zero off-by-one risk).
//
// Why offsets, not line/column: the parsed `SqlRange` carries 0-based
// line/column AND absolute offsets; Monaco markers are 1-based. Carrying the
// offset and letting Monaco do the conversion is the single source of truth.
//
// Design doc + cross-engine coercion matrix: Docs/TypeCheckDiagnostics.md.

import type { OperandLiteralSqlType } from '@/features/sql-resolver/types';

export type TypeCheckSeverity = 'warning' | 'info';

/** Monaco marker `code` shared by every type-mismatch diagnostic. Exported so
 *  the code-action layer can gate its quick-fix on "a type-mismatch was flagged
 *  here" without re-deriving the mismatch. */
export const TYPE_MISMATCH_DIAGNOSTIC_CODE = 'intellisense.type-mismatch' as const;

// Stable rule identifiers — used for the marker `code`, telemetry, and (later)
// per-rule suppression. The union grows additively.
//   numeric-vs-nonnumeric-string — all engines (numeric col vs non-numeric string)
//   text-vs-number               — PostgreSQL only (no implicit text↔number cast)
//   boolean-vs-nonboolean        — PostgreSQL only (col is the only true bool type)
export type TypeCheckRuleId =
  | 'numeric-vs-nonnumeric-string'
  | 'text-vs-number'
  | 'boolean-vs-nonboolean';

export type TypeCheckMarker = {
  /** Absolute UTF-16 code-unit offsets into the document source. */
  readonly startOffset: number;
  readonly endOffset: number;
  readonly message: string;
  readonly severity: TypeCheckSeverity;
  /** Monaco marker `code` — namespaced so it never collides with LSP codes. */
  readonly code: 'intellisense.type-mismatch';
  readonly ruleId: TypeCheckRuleId;
};

// One side of a comparison after type resolution. Mirrors the parser's
// `OperandType` arms, but every arm now carries a concrete `sqlType`
// (`'unknown'` when the resolver couldn't decide — the producer treats unknown
// as "don't flag", keeping it conservative).
export type ResolvedOperand =
  | {
      readonly kind: 'column';
      readonly sqlType: OperandLiteralSqlType;
      /** Display name for diagnostic messages (the bare column identifier). */
      readonly name: string;
    }
  | {
      readonly kind: 'literal';
      readonly sqlType: OperandLiteralSqlType;
      /** Raw literal text as written, e.g. `'abc'`, `N'5'`, `42`. */
      readonly text: string;
    }
  | {
      readonly kind: 'expression';
      readonly sqlType: OperandLiteralSqlType;
      readonly text: string;
    };

export type ResolvedComparison = {
  /** Absolute offsets of the whole comparison fragment. */
  readonly startOffset: number;
  readonly endOffset: number;
  readonly operator: string;
  readonly lhs: ResolvedOperand;
  readonly rhs?: ResolvedOperand;
};
